import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGameEngine } from './hooks/useGameEngine';
import StatsBar from './components/StatsBar';
import CharacterPortrait from './components/CharacterPortrait';
import PlayerAvatar from './components/PlayerAvatar';
import DialogueBox from './components/DialogueBox';
import AnswerBubble from './components/AnswerBubble';
import RoundResultsOverlay from './components/RoundResultsOverlay';
import YearTransitionOverlay from './components/YearTransitionOverlay';
import type { AnswerOption, GameVersion, GameEngineConfig, PlayerMood } from './types';
import {
  SHORT_STARTING_STABILITY,
  LONG_STARTING_STABILITY,
  DEATH_THRESHOLD,
} from './constants';
import { CHARACTERS } from './constants';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../hooks/useNotification';
import { useStarredQuestions } from './hooks/useStarredQuestions';
import { reportQuestion } from './api/gameApi';

function derivePlayerMood(
  phase: string,
  question: { options: AnswerOption[] } | null,
  chosenOptionId: string | null,
): PlayerMood {
  if (phase === 'RESOLVING' && question && chosenOptionId) {
    const chosen = question.options.find((o) => o.id === chosenOptionId);
    if (!chosen) return 'idle';
    return chosen.isCorrect ? 'happy' : 'sad';
  }
  return 'idle';
}

function getSelectedCharacter() {
  const id = localStorage.getItem('selectedCharacter') ?? 'cannonball';
  return CHARACTERS.find((c) => c.id === id) ?? CHARACTERS[0];
}

function buildTimerStorageKey(sessionId: string | null, questionId: string | undefined): string | null {
  if (!sessionId || !questionId) return null;
  return `game:questionTimerDeadline:${sessionId}:${questionId}`;
}

const GameSession: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { version } = useParams<{ version: string; sessionId: string }>();
  const forceNewSession = false;
  const isCustomQuiz = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('custom') === '1';
  }, [location.search]);
  const handleBackToMenu = useCallback(() => {
    if (isCustomQuiz) {
      const joinCode = sessionStorage.getItem('game:customQuizJoinCode');
      if (joinCode) {
        navigate(`/game?customLobby=${encodeURIComponent(joinCode)}`);
        return;
      }
    }
    navigate('/game');
  }, [isCustomQuiz, navigate]);

  const resumeSessionId = useMemo(() => {
    if (forceNewSession) return null;
    const params = new URLSearchParams(location.search);
    return params.get('session_id');
  }, [forceNewSession, location.search]);

  const { user, isAuthenticated } = useAuth();
  const { success, info, error } = useNotification();
  const { starredQuestions, addQuestion, removeQuestion, isQuestionStarred } = useStarredQuestions(user?.id ?? null);
  const gameVersion: GameVersion = version === 'long' ? 'long' : 'short';
  const character = useMemo(getSelectedCharacter, []);
  const [isSceneVisible, setIsSceneVisible] = useState(false);
  const [visitorMounted, setVisitorMounted] = useState(false);
  const [showYearTransition, setShowYearTransition] = useState(false);
  const [showDeathOverlay, setShowDeathOverlay] = useState(false);
  const [showWinScreen, setShowWinScreen] = useState(false);
  const [winScreenVisible, setWinScreenVisible] = useState(false);
  const [showGuestSavePrompt, setShowGuestSavePrompt] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDetailsText, setReportDetailsText] = useState('');
  const [selectedReportReason, setSelectedReportReason] = useState<'Wrong Answer' | 'Bad Wording' | 'Other' | null>(null);
  const hasRedirectedToResultsRef = useRef(false);

  const config: GameEngineConfig = useMemo(() => ({
    version: gameVersion,
    startingStability: gameVersion === 'long' ? LONG_STARTING_STABILITY : SHORT_STARTING_STABILITY,
    deathThreshold: DEATH_THRESHOLD,
  }), [gameVersion]);

  const { state, actions, loadError } = useGameEngine(
    config,
    resumeSessionId,
    { allowNewSessionCreation: !isCustomQuiz },
  );
  const { phase, stability, score, session, lastStabilityDelta, lastChosenOptionId } = state;
  const question = session.currentQuestion;
  const totalQuestions = session.questionResults.length || 0;

  useEffect(() => {
    actions.beginGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!state.backendSessionId && forceNewSession) {
      const params = new URLSearchParams(location.search);
      if (params.has('session_id')) {
        params.delete('session_id');
        const query = params.toString();
        navigate(query ? `${location.pathname}?${query}` : location.pathname, { replace: true });
      }
      return;
    }
    if (!state.backendSessionId) return;
    const params = new URLSearchParams(location.search);
    if (params.get('session_id') === state.backendSessionId) return;
    params.set('session_id', state.backendSessionId);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [forceNewSession, location.pathname, location.search, navigate, state.backendSessionId]);

  useEffect(() => {
    const fadeInTimer = window.setTimeout(() => {
      setIsSceneVisible(true);
    }, 10);
    return () => clearTimeout(fadeInTimer);
  }, []);

  const showOptions = phase === 'DECIDING';
  const isWalking = phase === 'WALKING';
  const isResolving = phase === 'RESOLVING';
  const isPlaying = (phase !== 'GAME_OVER' && phase !== 'ROUND_COMPLETE') || (phase === 'GAME_OVER' && !showDeathOverlay);

  const WALK_ANIM_DURATION = 1200;
  const WALKOUT_ANIM_DURATION = 1200;
  const NEXT_NPC_DELAY = 0;

  // Mirrors scoring timing (50s per question).
  const QUESTION_TIMER_SECONDS = 50;
  const TIMER_TICK_MS = 100;
  const [isWalkingOut, setIsWalkingOut] = useState(false);
  const nextQuestionTimerRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const activeTimerStorageKeyRef = useRef<string | null>(null);
  const [timeRemainingMs, setTimeRemainingMs] = useState(QUESTION_TIMER_SECONDS * 1000);
  const dyingButNotOverlayYet = phase === 'GAME_OVER' && !showDeathOverlay;
  const showInteractionUi = !isWalkingOut && (phase === 'DECIDING' || phase === 'RESOLVING' || dyingButNotOverlayYet);
  const showDialogueUi = !isWalkingOut && (phase === 'DECIDING' || phase === 'RESOLVING' || dyingButNotOverlayYet);
  const finalRoundEnded = gameVersion === 'long' && phase === 'ROUND_COMPLETE' && session.year >= 10;
  const customQuestionNumber = useMemo(() => {
    if (!isCustomQuiz || totalQuestions === 0) return 0;
    if (phase === 'ROUND_COMPLETE' || phase === 'GAME_OVER') {
      return Math.min(session.questionsAnswered, totalQuestions);
    }
    if (isResolving) {
      return Math.min(session.questionsAnswered, totalQuestions);
    }
    return Math.min(session.questionsAnswered + (question ? 1 : 0), totalQuestions);
  }, [isCustomQuiz, isResolving, phase, question, session.questionsAnswered, totalQuestions]);

  const goToResults = useCallback((endedBy: 'death' | 'final_year') => {
    if (hasRedirectedToResultsRef.current) return;
    hasRedirectedToResultsRef.current = true;

    const accuracyPercent = session.questionsAnswered > 0
      ? Math.round((session.correctCount / session.questionsAnswered) * 1000) / 10
      : 0;
    const resultsPath = state.backendSessionId
      ? `/game/results/${state.backendSessionId}`
      : '/game/results/local';

    navigate(resultsPath, {
      state: {
        endedBy,
        gameVersion,
        isCustomQuiz,
        finalYear: session.year,
        finalStability: stability,
        finalScore: state.score,
        questionsAnswered: session.questionsAnswered,
        questionsCorrect: session.correctCount,
        accuracyPercent,
        questionsTotal: totalQuestions || session.questionsAnswered,
      },
    });
  }, [
    gameVersion,
    navigate,
    session.correctCount,
    session.questionsAnswered,
    session.year,
    stability,
    state.backendSessionId,
    state.score,
    totalQuestions,
    isCustomQuiz,
  ]);

  useEffect(() => {
    if (!isResolving) {
      setIsWalkingOut(false);
    }
  }, [isResolving]);

  useEffect(() => {
    if (phase === 'GAME_OVER') {
      const t2 = window.setTimeout(() => setShowDeathOverlay(true), 2000);
      return () => {  clearTimeout(t2); };
    }
  }, [phase]);

  const handleViewResults = useCallback(() => {
    setShowWinScreen(true);
    window.setTimeout(() => setWinScreenVisible(true), 50);
  }, []);

  useEffect(() => {
    if (phase === 'WALKING') {
      hasRedirectedToResultsRef.current = false;
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      if (nextQuestionTimerRef.current !== null) {
        window.clearTimeout(nextQuestionTimerRef.current);
      }
      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const clearPersistedTimer = useCallback(() => {
    if (!activeTimerStorageKeyRef.current) return;
    try {
      window.sessionStorage.removeItem(activeTimerStorageKeyRef.current);
    } catch {
      // Ignore storage unavailability.
    }
    activeTimerStorageKeyRef.current = null;
  }, []);

  const visitorMoving = isWalking || isWalkingOut;
  const playerMood = derivePlayerMood(phase, question, lastChosenOptionId);
  const questionIsSaved = question ? isQuestionStarred(question.questionId) : false;

  useEffect(() => {
    if (!isPlaying || !question) {
      setVisitorMounted(false);
      return;
    }
    setVisitorMounted(false);
    const raf = requestAnimationFrame(() => setVisitorMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, question]);

  const handleToggleSaveQuestion = () => {
    if (!question) return;
    if (!isAuthenticated) {
      setShowGuestSavePrompt(true);
      return;
    }
    if (questionIsSaved) {
      const savedEntry = starredQuestions.find((item) => item.questionId === question.questionId);
      if (savedEntry) {
        removeQuestion(savedEntry.id);
        info('Question removed from saved list.');
      }
      return;
    }
    const selectedAnswerText = lastChosenOptionId
      ? question.options.find((option) => option.id === lastChosenOptionId)?.text
      : undefined;
    addQuestion(question, selectedAnswerText);
    success('Question saved.');
  };

  const handleNextQuestionClick = useCallback(() => {
    if (!isResolving || isWalkingOut) return;
    setIsWalkingOut(true);
    if (nextQuestionTimerRef.current !== null) {
      window.clearTimeout(nextQuestionTimerRef.current);
    }
    // The visitor sprite is hidden on narrow screens, so the walk-out
    // animation would just be dead time there — skip it.
    const isNarrow = window.matchMedia('(max-width: 639px)').matches;
    const delay = isNarrow ? 0 : WALKOUT_ANIM_DURATION + NEXT_NPC_DELAY;
    nextQuestionTimerRef.current = window.setTimeout(() => {
      actions.nextQuestion();
      nextQuestionTimerRef.current = null;
    }, delay);
  }, [NEXT_NPC_DELAY, WALKOUT_ANIM_DURATION, actions, isResolving, isWalkingOut]);

  useEffect(() => {
    if (!isResolving || isWalkingOut || showReportModal || showGuestSavePrompt) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      handleNextQuestionClick();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNextQuestionClick, isResolving, isWalkingOut, showGuestSavePrompt, showReportModal]);

  useEffect(() => {
    if (phase === 'RESOLVING' || phase === 'ROUND_COMPLETE' || phase === 'GAME_OVER') {
      clearPersistedTimer();
    }
  }, [clearPersistedTimer, phase]);

  useEffect(() => {
    const readyToCountDown = phase === 'DECIDING' && !!question && question.options.length > 0 && !isWalkingOut;
    if (!readyToCountDown) {
      clearCountdown();
      return;
    }

    const storageKey = buildTimerStorageKey(state.backendSessionId, question?.questionId);
    activeTimerStorageKeyRef.current = storageKey;
    const now = Date.now();
    let deadline = now + QUESTION_TIMER_SECONDS * 1000;
    if (storageKey) {
      try {
        const storedDeadline = window.sessionStorage.getItem(storageKey);
        const existingDeadline = storedDeadline !== null ? Number(storedDeadline) : NaN;
        if (Number.isFinite(existingDeadline)) {
          const maxAllowedDeadline = now + QUESTION_TIMER_SECONDS * 1000;
          deadline = Math.min(existingDeadline, maxAllowedDeadline);
          if (deadline !== existingDeadline) {
            window.sessionStorage.setItem(storageKey, String(deadline));
          }
        } else {
          window.sessionStorage.setItem(storageKey, String(deadline));
        }
      } catch {
        // Ignore storage access failures and continue with in-memory timer.
      }
    }

    const initialRemaining = Math.max(0, deadline - now);
    setTimeRemainingMs(initialRemaining);
    if (initialRemaining <= 0) {
      setTimeRemainingMs(0);
      return;
    }

    countdownIntervalRef.current = window.setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      if (remaining <= 0) {
        setTimeRemainingMs(0);
        if (countdownIntervalRef.current !== null) {
          window.clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        return;
      }
      setTimeRemainingMs(remaining);
    }, TIMER_TICK_MS);

    return () => {
      if (countdownIntervalRef.current !== null) {
        window.clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [
    QUESTION_TIMER_SECONDS,
    TIMER_TICK_MS,
    clearCountdown,
    isWalkingOut,
    phase,
    question,
    state.backendSessionId,
  ]);

  const timerSecondsLeft = Math.ceil(timeRemainingMs / 1000);

  const handleReportSubmit = async () => {
    if (!question) return;
    if (!selectedReportReason) return;
    const detailText = reportDetailsText.trim();
    try {
      await reportQuestion(
        question.questionId,
        selectedReportReason,
        detailText.length > 0 ? detailText : undefined,
      );
      if (selectedReportReason === 'Wrong Answer') {
        info('Report submitted: wrong answer.');
      } else if (selectedReportReason === 'Bad Wording') {
        info('Report submitted: bad wording.');
      } else {
        info('Report submitted: other issue.');
      }
    } catch (err) {
      console.error('Failed to submit report:', err);
      error('Could not submit report right now.');
    } finally {
      setReportDetailsText('');
      setSelectedReportReason(null);
      setShowReportModal(false);
    }
  };

  return (
    <div
      className="relative flex min-h-0 flex-col overflow-hidden h-screen max-h-screen"
      style={{
        backgroundImage: 'url(/backgrounds/game-court-bg.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center 75%',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#1a1a2e',
      }}
    >

      {loadError && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl bg-[#1b2a41] border border-white/10 shadow-2xl p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-2 font-caudex">Unable to Start Game</h2>
            <p className="text-sm text-white/70 mb-6 font-caudex">{loadError}</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => navigate('/game')}
                className="px-4 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
              >
                Back to Menu
              </button>
              <button
                onClick={actions.beginGame}
                className="px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage: dialogue/answer overlay + characters.
        The overlay is bounded top AND bottom by this container, so the
        answer bubble can never extend past the Next Question button. */}
      <div className="relative flex-1 min-h-0 flex flex-col">
        <style>{`
          @media (max-width: 639px) {
            .dialogue-answer-wrapper {
              top: 64px !important;
              padding-left: clamp(12px, 4vw, 24px) !important;
              padding-right: clamp(12px, 4vw, 24px) !important;
            }
            .answer-bubble-spacer {
              display: none;
            }
          }
          @media (max-height: 500px) {
            .answer-bubble-spacer {
              display: none;
            }
          }
        `}</style>

        {/* Dialogue + Answer Bubble */}
        <div
          className="flex flex-col dialogue-answer-wrapper"
          style={{
            position: 'absolute',
            top: 'clamp(10px, 2vh, 20px)',
            bottom: 0,
            left: 0,
            right: 0,
            paddingLeft: 'clamp(20px, 3vw, 60px)',
            paddingRight: 'clamp(20px, 3vw, 60px)',
            paddingBottom: '22px', // room for the bubble's tail
            gap: 'clamp(8px, 1.5vh, 16px)',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          {showDialogueUi && question ? (
            <div style={{ pointerEvents: 'auto', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
              <DialogueBox
                characterName={question.characterName}
                characterImage={question.characterImage}
                text={question.dialogueText}
                visible={showDialogueUi}
                countdownSeconds={phase === 'DECIDING' ? Math.max(0, timerSecondsLeft) : null}
                countdownTotalSeconds={QUESTION_TIMER_SECONDS}
                onSaveClick={handleToggleSaveQuestion}
                isSaved={questionIsSaved}
                onReportClick={() => {
                  setReportDetailsText('');
                  setSelectedReportReason(null);
                  setShowReportModal(true);
                }}
              />
            </div>
          ) : null}
          {isPlaying && showInteractionUi && (
            <>
              {/* Shrinkable spacer: keeps the old 15vh breathing room on
                  large screens, gives it up first when space is tight. */}
              <div
                className="answer-bubble-spacer"
                style={{ flex: '0 1 clamp(0px, 15vh, 200px)', minHeight: 0 }}
              />
              <div
                className="answer-bubble-wrapper"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: '0 1 auto',
                  minHeight: 0,
                  pointerEvents: 'auto',
                }}
              >
                <AnswerBubble
                  options={question?.options ?? []}
                  onChoose={actions.decide}
                  showOptions={showOptions}
                  isResolving={isResolving || dyingButNotOverlayYet}
                  chosenId={lastChosenOptionId}
                  visible
                />
              </div>
            </>
          )}
        </div>

        {/* Characters */}
        <div className="flex-1 min-h-0 flex items-end justify-end sm:justify-between px-4 sm:px-8 pb-0 -mb-12">
          {isPlaying ? (
            <>
              {/* Visitor */}
              <div
                className="hidden sm:block shrink-0 transition-transform"
                style={{
                  transitionDuration: isWalking ? `${WALK_ANIM_DURATION}ms` : isWalkingOut ? `${WALKOUT_ANIM_DURATION}ms` : '0ms',
                  transitionTimingFunction: isWalkingOut ? 'ease-in' : 'linear',
                  transform: !visitorMounted
                    ? 'translateX(-40vw)'
                    : isWalking
                      ? 'translateX(0)'
                      : isWalkingOut
                        ? 'translateX(-40vw)'
                        : 'translateX(0)',
                }}
              >
                {question && (
                  <CharacterPortrait
                    name={question.characterName}
                    image={question.characterImage}
                    isWalking={visitorMoving}
                    mirrored={isWalkingOut}
                  />
                )}
              </div>

              {/* Player */}
              <div className="shrink-0">
                <PlayerAvatar
                  mood={playerMood}
                  characterImage={character.image}
                  characterName={character.name}
                  characterReactions={character.reactions}
                />
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Next Question button*/}
      <div className="shrink-0 flex justify-center pb-2 relative z-20" style={{ height: '52px' }}>
        {isResolving && !isWalkingOut && (
          <button
            onClick={handleNextQuestionClick}
            className="px-6 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex shadow-lg"
            style={{ touchAction: 'manipulation' }}
          >
            Next Question
          </button>
        )}
      </div>

      {/* Bottom bar */}
      <div className="shrink-0 px-3 pb-2 sm:px-5 sm:pb-3">
        <StatsBar
          score={score}
          vitality={stability}
          lastDelta={isResolving ? lastStabilityDelta : null}
          questionResults={session.questionResults}
          currentQuestionIndex={session.questionsAnswered}
          year={session.year}
          sessionLabel={isCustomQuiz ? 'Custom Game' : undefined}
          progressLabel={
            isCustomQuiz && totalQuestions > 0
              ? `Question ${customQuestionNumber} of ${totalQuestions}`
              : undefined
          }
          showQuestionBadges={!isCustomQuiz}
          showVitality={gameVersion === 'long'}
        />
      </div>

      {/* Overlays */}
      {showDeathOverlay && (
        <RoundResultsOverlay
          correctCount={session.correctCount}
          total={totalQuestions}
          stability={stability}
          score={score}
          year={session.year}
          dead
          primaryLabel="View Results"
          onPrimary={() => goToResults('death')}
        />
      )}

      {phase === 'ROUND_COMPLETE' && gameVersion === 'short' && (
        <RoundResultsOverlay
          correctCount={session.correctCount}
          total={totalQuestions}
          stability={stability}
          score={score}
          year={session.year}
          dead={session.correctCount < Math.ceil(totalQuestions / 2)}
          isShortMode
          primaryLabel="Back to Menu"
          onPrimary={handleBackToMenu}
        />
      )}

      {phase === 'ROUND_COMPLETE' && gameVersion === 'long' && session.year < 10 && !showYearTransition && (
        <RoundResultsOverlay
          correctCount={session.correctCount}
          total={totalQuestions}
          stability={stability}
          score={score}
          year={session.year}
          dead={stability <= 0}
          primaryLabel={stability > 0 ? 'Next Year' : 'View Results'}
          onPrimary={stability > 0 ? () => setShowYearTransition(true) : () => goToResults('death')}
        />
      )}

      {phase === 'ROUND_COMPLETE' && gameVersion === 'long' && session.year < 10 && showYearTransition && (
        <YearTransitionOverlay
          nextYear={session.year + 1}
          stability={stability}
          correctCount={session.correctCount}
          total={totalQuestions}
          onContinue={() => { setShowYearTransition(false); actions.startNextRound(); }}
        />
      )}

      {finalRoundEnded && !showWinScreen && (
        <FinalYearCompleteOverlay
          year={session.year}
          onViewResults={handleViewResults}
          onBackToMenu={() => navigate('/game')}
        />
      )}

      {finalRoundEnded && showWinScreen && (
        <div
          className="absolute inset-0 z-30 cursor-pointer"
          style={{ opacity: winScreenVisible ? 1 : 0, transition: 'opacity 1.2s ease' }}
          onClick={() => goToResults('final_year')}
        >
          <img src='/backgrounds/winning.png' alt="Victory screen" className="w-full h-full object-cover" />
        </div>
      )}

      {showGuestSavePrompt && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-sm rounded-2xl bg-[#1b2a41] border border-white/10 shadow-2xl p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-2 font-caudex">Save Questions</h2>
            <p className="text-sm text-white/70 mb-6 font-caudex">
              Register or log in to save starred questions for review.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowGuestSavePrompt(false)}
                className="px-4 py-2 rounded-full border border-white/30 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-4 py-2 rounded-full bg-teal-500 hover:bg-teal-600 transition font-semibold text-sm text-white font-caudex"
              >
                Register
              </button>
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
              >
                Log In
              </button>
            </div>
          </div>
        </div>
      )}

      {showReportModal && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-md rounded-2xl bg-[#1b2a41] border border-white/10 shadow-2xl p-6 text-center">
            <button
              onClick={() => {
                setReportDetailsText('');
                setSelectedReportReason(null);
                setShowReportModal(false);
              }}
              className="absolute top-3 right-3 w-7 h-7 rounded-md border border-white/30 text-white/80 hover:text-white hover:bg-white/10 transition text-sm leading-none"
              aria-label="Close report modal"
            >
              ×
            </button>
            <h2 className="text-xl font-bold text-white mb-2 font-caudex">Report Question</h2>
            <p className="text-sm text-white/70 mb-6 font-caudex">
              Tell us what issue you found with this question.
            </p>
            <div className="flex flex-col gap-3">
              <select
                value={selectedReportReason ?? ''}
                onChange={(e) => setSelectedReportReason((e.target.value || null) as 'Wrong Answer' | 'Bad Wording' | 'Other' | null)}
                className="w-full px-3 py-2.5 rounded-xl bg-[#27315D] border border-[#2E3A6B] text-white text-sm outline-none focus:ring-2 focus:ring-cyan-300/40"
              >
                <option value="" disabled>
                  Select a reason
                </option>
                <option value="Wrong Answer">Wrong Answer</option>
                <option value="Bad Wording">Bad Wording</option>
                <option value="Other">Other</option>
              </select>
              <textarea
                value={reportDetailsText}
                onChange={(e) => setReportDetailsText(e.target.value)}
                placeholder="Details (optional)"
                rows={3}
                className="w-full resize-none px-3 py-2.5 rounded-xl bg-[#27315D] border border-[#2E3A6B] text-white text-sm placeholder:text-white/50 outline-none focus:ring-2 focus:ring-cyan-300/40"
              />
              <button
                onClick={handleReportSubmit}
                disabled={!selectedReportReason}
                className="w-full px-4 py-2.5 rounded-xl bg-[#27315D] border border-[#2E3A6B] hover:bg-[#2d3970] transition font-semibold text-sm text-white font-caudex disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`pointer-events-none absolute inset-0 z-40 bg-black transition-opacity duration-500 ease-in-out ${
          isSceneVisible ? 'opacity-0' : 'opacity-100'
        }`}
      />
    </div>
  );
};


interface FinalYearCompleteOverlayProps {
  year: number;
  onViewResults: () => void;
  onBackToMenu: () => void;
}

const FinalYearCompleteOverlay: React.FC<FinalYearCompleteOverlayProps> = ({
  year,
  onViewResults,
  onBackToMenu,
}) => (
  <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
    <div className="w-full max-w-xl rounded-2xl bg-[#1b2a41]/95 border border-white/10 shadow-2xl p-6 sm:p-8 text-center">
      <h2
        className="text-[#FFE896] mb-2"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontWeight: 400,
          fontSize: 'clamp(30px, 4.6vw, 52px)',
          lineHeight: 1.2,
        }}
      >
        Final Year Complete
      </h2>
      <p className="text-white/75 text-sm sm:text-base mb-6 font-caudex">
        Humanity has reached SDG 2030.
        <br />
        View the final trial outcome and performance statisticss.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button onClick={onViewResults} className="btn-game-classic">
          View Game Results
        </button>
        <button
          onClick={onBackToMenu}
          className="px-5 py-2 rounded-full border border-cyan-200/60 hover:bg-white/10 transition font-semibold text-sm text-white font-caudex"
        >
          Back to Menu
        </button>
      </div>
    </div>
  </div>
);

export default GameSession;
